import React, { useContext, useMemo } from 'react';
import {
  ProductTransaction,
  productTransactionTypeToCustomFieldEntitySubType,
} from '../../../../../../../../types/productTransaction';
import { Grid } from '@mui/material';
import { ProductMasterData } from '../../../../../../../../types/product_master_data/productMasterData';
import { Container } from '../../../../../../../../types/container';
import { CustomFieldContext } from '../../../../../../../../context/CustomFieldContext';
import { CustomField, CustomFieldEntityType, CustomFieldType } from '../../../../../../../../types/custom_field';
import { OrderContext } from '../../../../../../../../context/OrderContext';
import CustomFieldInput from '../../../../../../Common/CustomFieldInput';
import { BusinessCentral } from '../../../../../../../../types/common/integration/bc';
import { UserContext } from '../../../../../../../../context/UserContext';
import { Unilever } from '../../../../../../../../types/customer/DHL/Unilever/Unilever';
import { ContactContext } from '../../../../../../../../context/ContactContext';
import { ProductMasterDataContext } from '../../../../../../../../context/ProductMasterDataContext';

interface ProductTransactionCustomFieldsProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;

  productMasterData?: ProductMasterData;
  container?: Container;

  disabled?: boolean;
}

export default function ProductTransactionCustomFields({
  transaction,
  setTransaction,
  productMasterData: pmd,
  disabled = false,
}: ProductTransactionCustomFieldsProps) {
  const { orders } = useContext(OrderContext);
  const { customFields } = useContext(CustomFieldContext);
  const { companyUsers } = useContext(UserContext);
  const { contacts } = useContext(ContactContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const items = useMemo(() => {
    const entityTypes = new Set<CustomFieldEntityType>([CustomFieldEntityType.productTransaction]);
    if (transaction.product.containerId) entityTypes.add(CustomFieldEntityType.containerTransaction);

    return [...customFields.values()].filter(cf => {
      if (cf.hidden) return false;

      if (entityTypes.has(cf.entityType)) {
        if (
          (!cf.entitySubtype ||
            cf.entitySubtype === productTransactionTypeToCustomFieldEntitySubType(transaction.type)) &&
          (cf.entityParentType === transaction.parentType || !cf.entityParentType)
        )
          return true;
      }

      return false;
    });
  }, [customFields, transaction]);

  const order = orders.get(transaction.parentId || '');

  const intergrationGenerated = (field: CustomField) => {
    return (field.generated && BusinessCentral.isPurchaseOrder(order)) || Unilever.disabledCustomField(field);
  };

  const generatedAndEmpty = (field: CustomField) => {
    return (
      field.generated &&
      !transaction.customFields.get(field.id)?.value &&
      (BusinessCentral.isElterosInvoice(order) ||
        BusinessCentral.isJob(order) ||
        BusinessCentral.isItemJournal(order)) &&
      !transaction.product.fromContainerId
    );
  };

  return (
    <Grid item xs={12}>
      <Grid container rowSpacing={1} columnSpacing={1}>
        {items
          .sort((a, b) => a.index - b.index)
          .map(field => {
            if (generatedAndEmpty(field)) return null;

            return (
              <Grid item xs={field.type === CustomFieldType.bool ? 12 : 6} my={'auto'}>
                <CustomFieldInput
                  key={field.id}
                  item={field}
                  value={transaction.customFields.get(field.id)?.value}
                  entity={transaction}
                  onChange={value => {
                    const item = value.customFields.get(field.id);
                    if (!item) return;
                    setTransaction(transaction => transaction.withCustomField({ ...item, value: item.value }));
                  }}
                  disabled={disabled || generatedAndEmpty(field) || intergrationGenerated(field)}
                  pmdId={transaction.product.pmdId}
                  users={companyUsers}
                  contacts={contacts}
                  productMasterData={productMasterData}
                />
              </Grid>
            );
          })}
      </Grid>
    </Grid>
  );
}
